import type { LineAmountType, LineItemInput, TaxRate } from './types.js';
import { parseAmount } from './normalize.js';

export interface Totals {
  sub: number;
  tax: number;
  total: number;
}

/** Gross line value before tax: qty x unit price, less the percentage discount. */
export function lineAmount(line: LineItemInput): number {
  const qty = parseAmount(line.qty);
  const price = parseAmount(line.price);
  const discount = parseAmount(line.disc);
  return qty * price * (1 - discount / 100);
}

function rateFor(taxRates: TaxRate[], taxType: string): number {
  return taxRates.find((t) => t.type === taxType)?.rate ?? 0;
}

/**
 * Document totals under each of Xero's three LineAmountTypes.
 *
 * Inclusive back-solves the net from the gross so the displayed total matches
 * what Xero stores, rather than adding tax on top of a tax-inclusive figure.
 */
export function computeTotals(
  lines: LineItemInput[],
  amountsAre: LineAmountType,
  taxRates: TaxRate[],
): Totals {
  let sub = 0;
  let tax = 0;

  for (const line of lines) {
    const gross = lineAmount(line);
    const rate = rateFor(taxRates, line.taxType);

    if (amountsAre === 'Inclusive') {
      const net = gross / (1 + rate / 100);
      sub += net;
      tax += gross - net;
    } else if (amountsAre === 'NoTax') {
      sub += gross;
    } else {
      sub += gross;
      tax += (gross * rate) / 100;
    }
  }

  return { sub, tax, total: sub + tax };
}
